import type React from "react";
import { useState, useRef, useMemo } from "react";
import {
  MoreHorizontal,
  Pencil,
  Shield,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Download,
  Upload,
  Loader2,
  Lock,
  Unlock,
} from "lucide-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrgNames, knownRoleName, knownDepartmentName } from "@/hooks/useOrgNames";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getStatusConfig } from "@/config/status-registry";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";

import { SearchableCombobox } from "@/components/shared/SearchableCombobox";
import { ComboboxFK } from "@/components/shared/ComboboxFK";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn, getUploadUrl, extractErrorMessage } from "@/lib/utils";
import { Card } from "@/components/ui/card";

import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { trpcClient } from "@/lib/trpc";
import { useRowSelection } from "@/hooks/useRowSelection";
import type { User, PaginatedData } from "@/shared/schemas";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useRoleLookup } from "@/hooks/useFKLookup";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useImportRefs } from "@/hooks/useImportRefs";
import { normKey, ROW_KEY, type ImportResult, type ImportRowResult } from "@/lib/import-kit";
import { emsTabClass } from "@/components/ui/ems-tabs";

export const getRoleLabel = (role: string) => {
  switch (role?.toUpperCase()) {
    case "ADMIN":
      return "Quản trị viên";
    case "MANAGER":
      return "Quản lý";
    case "SALES_MANAGER":
      return "Trưởng phòng KD";
    case "SALES":
      return "Kinh doanh";
    case "PLANNER":
      return "Kế hoạch";
    case "WAREHOUSE":
      return "Thủ kho";
    case "DELIVERY":
      return "Giao hàng";
    case "QC":
      return "Chất lượng (QC)";
    case "TECH":
      return "Kỹ thuật";
    case "OPERATOR":
      return "Vận hành";
    case "USER":
      return "Nhân viên";
    default:
      return knownRoleName(role) || role;
  }
};

export const getDepartmentLabel = (dept: string) => {
  switch (dept?.toUpperCase()) {
    case "IT":
      return "Công Nghệ Thông Tin";
    case "SALES":
      return "Kinh Doanh";
    case "PROD":
      return "Sản Xuất";
    case "QC":
      return "Kiểm Soát Chất Lượng";
    case "WH":
      return "Kho Vận";
    case "TECH":
      return "Kỹ Thuật";
    case "MAINT":
      return "Bảo Trì";
    case "BOARD":
      return "Ban Giám Đốc";
    case "FIN":
      return "Kế Toán";
    case "HR":
      return "Hành Chính Nhân Sự";
    default:
      return knownDepartmentName(dept) || dept;
  }
};

/** Cột Excel nhân sự — dùng chung cho xuất, file mẫu và nhập (04/11/2026); Vai trò / Phòng ban khớp danh mục */
const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "employeeCode", label: "Mã NV", width: 14 },
  { key: "fullName", label: "Họ và tên", width: 25, required: true },
  { key: "email", label: "Email", width: 25, required: true },
  {
    key: "role",
    label: "Vai trò",
    width: 18,
    required: true,
    ref: "role",
    formatter: (_val, row: any) => getRoleLabel(row.role),
  },
  {
    key: "department",
    label: "Phòng ban",
    width: 22,
    required: true,
    ref: "department",
    formatter: (_val, row: any) => getDepartmentLabel(row.department),
  },
  {
    key: "status",
    label: "Trạng thái",
    width: 14,
    formatter: (_val, row: any) =>
      getStatusConfig("UserStatus", row.status)?.label || row.status,
  },
];

export default function UserListPage() {
  useOrgNames();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { isAdmin, hasPermission } = useRoleAccess();
  const canViewUsers = isAdmin || hasPermission("users", "view");
  const canCreateUsers = isAdmin || hasPermission("users", "create");
  const canUpdateUsers = isAdmin || hasPermission("users", "update");
  const canExportUsers = isAdmin || hasPermission("users", "export");
  const canImportUsers = isAdmin || hasPermission("users", "import");
  const [showImport, setShowImport] = useState(false);

  // Dynamic lookup for roles and departments
  const { options: roleOptions } = useRoleLookup();
  const { options: departmentOptions } = useConfigLookup("departments");

  // Refactored to useServerListFilter
  const {
    items: paginatedUsers,
    meta,
    isLoading,
    error,
    searchQuery,
    filterValues,
    currentPage,
    pageSize,
    showFilter,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<User>({
    queryKey: ["users"],
    queryFn: async (params) => {
      if (!canViewUsers) {
        return { items: [], meta: { total: 0, page: 1, limit: params.limit, totalPages: 1 } };
      }
      const apiParams: Record<string, any> = {
        page: params.page,
        limit: params.limit,
      };
      if (params.search) apiParams.search = params.search;
      if (params.role) apiParams.role = params.role;
      if (params.status) apiParams.status = params.status;
      if (params.department) apiParams.department = params.department;
      if (params.section) apiParams.section = params.section;
      if (params.employeeCode) apiParams.employeeCode = params.employeeCode;
      if (params.phone) apiParams.phone = params.phone;
      if (params.sortBy) {
        apiParams.sortBy = params.sortBy;
        apiParams.sortOrder = params.sortOrder;
      }
      return (await trpcClient.users.list.query(
        apiParams,
      )) as PaginatedData<User>;
    },
    defaultLimit: 500,
  });

  const users = paginatedUsers;
  const total = meta.total;
  const totalPages = meta.totalPages;

  // Bộ lọc inline cho phép chọn nhiều giá trị (mảng); ô chọn trên header chỉ hiển thị 1 giá trị
  const singleFilterValue = (v: unknown): string =>
    Array.isArray(v) ? (v.length === 1 ? String(v[0]) : "all") : (v as string) || "all";
  const roleFilter = singleFilterValue(filterValues.role);
  const statusFilter = singleFilterValue(filterValues.status);
  const departmentFilter = singleFilterValue(filterValues.department);
  const positionFilter = (filterValues.section as string) || "all";

  const userFilterFields: FilterField[] = useMemo(() => [
    { key: "employeeCode", label: "Mã NV", type: "text", placeholder: "Mã NV..." },
    { key: "search", label: "Thông tin chung", type: "text", placeholder: "Tên, email..." },
    { key: "phone", label: "Số điện thoại", type: "text", placeholder: "SĐT..." },
    {
      key: "role",
      label: "Vai trò",
      type: "combobox-multi",
      options: roleOptions.length > 0
        ? roleOptions.map((r) => ({ value: r.id, label: r.label }))
        : [
            { value: "ADMIN", label: "Quản trị viên" },
            { value: "MANAGER", label: "Quản lý" },
            { value: "SALES_MANAGER", label: "Trưởng phòng KD" },
            { value: "SALES", label: "Kinh doanh" },
            { value: "PLANNER", label: "Kế hoạch" },
            { value: "WAREHOUSE", label: "Thủ kho" },
            { value: "DELIVERY", label: "Giao hàng" },
            { value: "QC", label: "KCS (QC)" },
            { value: "TECH", label: "Kỹ thuật" },
            { value: "OPERATOR", label: "Vận hành máy" },
            { value: "USER", label: "Nhân viên" },
          ],
    },
    {
      key: "department",
      label: "Phòng ban",
      type: "combobox-multi",
      options: departmentOptions.length > 0
        ? departmentOptions.map((d) => ({ value: d.id, label: d.label }))
        : [
            { value: "IT", label: "CNTT" },
            { value: "SALES", label: "Kinh Doanh" },
            { value: "PROD", label: "Sản Xuất" },
            { value: "QC", label: "KCS" },
            { value: "WH", label: "Kho Vận" },
            { value: "TECH", label: "Kỹ Thuật" },
            { value: "MAINT", label: "Bảo Trì" },
            { value: "BOARD", label: "Ban Giám Đốc" },
            { value: "FIN", label: "Kế Toán" },
            { value: "HR", label: "Hành Chính Nhân Sự" },
          ],
    },
    {
      key: "status",
      label: "Trạng thái",
      type: "combobox-multi",
      options: [
        { value: "ACTIVE", label: "Hoạt động" },
        { value: "INACTIVE", label: "Vô hiệu" },
      ],
    },
  ], [roleOptions, departmentOptions]);

  // Bộ lọc theo cột đã được máy chủ xử lý nên danh sách hiển thị chính là kết quả trả về
  const filteredUsers = paginatedUsers;

  const handleFilterChange = (key: string, value: string) => {
    if (value === "all") {
      const newFilter = { ...filterValues };
      delete newFilter[key];
      onFilterChange(newFilter);
    } else {
      onFilterChange({ ...filterValues, [key]: value });
    }
  };

  const rowIds = paginatedUsers.map((u: any) => u.userId);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    clearSelection,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    selectedCount,
  } = useRowSelection(rowIds);

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    return [
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
      {
        label: "Vô hiệu hóa",
        icon: <Lock className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              trpcClient.users.update.mutate({
                id,
                data: { status: "INACTIVE" },
              }),
            actionName: "vô hiệu hóa",
            itemType: "người dùng",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["users"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn vô hiệu hóa ${selectedCount} người dùng đã chọn?`,
          });
        },
        variant: "destructive",
      },
      {
        label: "Mở khóa",
        icon: <Unlock className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              trpcClient.users.update.mutate({
                id,
                data: { status: "ACTIVE" },
              }),
            actionName: "mở khóa",
            itemType: "người dùng",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["users"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn mở khóa ${selectedCount} người dùng đã chọn?`,
          });
        },
        className:
          "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent",
      },
    ];
  };

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "NhanSu_APV",
    queryFn: trpcClient.users.list.query,
    currentFilters: {
      search: searchQuery || (filterValues.search as string) || undefined,
      role: (filterValues.role as any) && (filterValues.role as any).length ? (filterValues.role as any) : undefined,
      status: (filterValues.status as any) && (filterValues.status as any).length ? (filterValues.status as any) : undefined,
      department: (filterValues.department as any) && (filterValues.department as any).length ? (filterValues.department as any) : undefined,
      section: positionFilter !== "all" ? positionFilter : undefined,
    },
    selectedIds,
    idKey: "userId",
  });

  // Nhập Excel nhân sự (04/11/2026): khớp người đã có theo email / mã NV trên toàn bộ danh sách (trước chỉ trang đang xem);
  // Vai trò, Phòng ban khớp danh mục theo mã / tên (trước gửi nguyên nhãn và gán cứng PROD); báo cáo từng dòng.
  const importRefs = useImportRefs();
  const userImportRefs = useMemo(() => {
    const withLabels = (def: any, labelOf: (v: string) => string) => ({
      ...def,
      load: async () => (await def.load()).map((o: any) => ({ ...o, keys: [...o.keys, labelOf(o.value)] })),
    });
    return {
      role: withLabels(importRefs.role, getRoleLabel),
      department: withLabels(importRefs.department("code"), getDepartmentLabel),
    };
  }, [importRefs]);
  const handleImport = async (validRows: Record<string, unknown>[]): Promise<ImportResult> => {
    const listRes: any = await trpcClient.users.list.query({ limit: 10000 } as any).catch(() => null);
    const all: any[] = Array.isArray(listRes) ? listRes : listRes?.items || listRes?.data || [];
    const results: ImportRowResult[] = [];
    for (const row of validRows) {
      const rowNo = Number(row[ROW_KEY]) || 0;
      let employeeCode = String(row.employeeCode ?? "").trim();
      if (employeeCode && !employeeCode.startsWith("A") && !isNaN(Number(employeeCode))) {
        employeeCode = `A${employeeCode.padStart(4, "0")}`;
      }
      const email = String(row.email ?? "").trim();
      const statusRaw = normKey(row.status ?? "");
      const status = !statusRaw || ["hoat dong", "active"].includes(statusRaw) ? "ACTIVE" : "INACTIVE";
      const data = {
        fullName: String(row.fullName ?? "").trim(),
        role: String(row.role ?? ""),
        department: String(row.department ?? ""),
        status,
      };
      try {
        const existing = all.find(
          (u) => normKey(u.email) === normKey(email) || (employeeCode && u.employeeCode === employeeCode),
        );
        if (existing) {
          await trpcClient.users.update.mutate({ id: existing.userId, data });
          results.push({ row: rowNo, code: email, status: "updated" });
        } else {
          await trpcClient.users.create.mutate({
            ...data,
            userId: employeeCode || email.split("@")[0],
            employeeCode,
            email,
            position: "",
            password: "123456",
          } as any);
          results.push({ row: rowNo, code: email, status: "created" });
        }
      } catch (err) {
        results.push({ row: rowNo, code: email, status: "failed", message: extractErrorMessage(err) || "Lỗi" });
      }
    }
    await queryClient.invalidateQueries({ queryKey: ["users"] });
    await queryClient.invalidateQueries({ queryKey: ["users-lookup"] });
    return { rows: results };
  };

  const handleEdit = (user: any) => {
    navigate({ to: `/users/${user.userId}/edit` });
  };

  const getRoleBadge = (role: string) => {
    switch (role?.toUpperCase()) {
      case "ADMIN":
        return (
          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-none shadow-none font-semibold px-2">
            Quản trị viên
          </Badge>
        );
      case "MANAGER":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none shadow-none font-semibold px-2">
            Quản lý
          </Badge>
        );
      case "SALES_MANAGER":
        return (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-none shadow-none font-semibold px-2">
            Trưởng phòng KD
          </Badge>
        );
      case "SALES":
        return (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none shadow-none font-semibold px-2">
            Kinh doanh
          </Badge>
        );
      case "PLANNER":
        return (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none shadow-none font-semibold px-2">
            Kế hoạch
          </Badge>
        );
      case "WAREHOUSE":
        return (
          <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-100 border-none shadow-none font-semibold px-2">
            Thủ kho
          </Badge>
        );
      case "DELIVERY":
        return (
          <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 border-none shadow-none font-semibold px-2">
            Giao hàng
          </Badge>
        );
      case "QC":
        return (
          <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-100 border-none shadow-none font-semibold px-2">
            QC
          </Badge>
        );
      case "TECH":
        return (
          <Badge className="bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100 border-none shadow-none font-semibold px-2">
            Kỹ thuật
          </Badge>
        );
      case "OPERATOR":
        return (
          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-none shadow-none font-semibold px-2">
            Vận hành
          </Badge>
        );
      case "USER":
        return (
          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-none shadow-none font-semibold px-2">
            Nhân viên
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="font-semibold" title={role}>
            {getRoleLabel(role)}
          </Badge>
        );
    }
  };

  if (!canViewUsers) {
    return (
      <div className="flex-1 p-4 md:p-6">
        <Card className="p-8 text-center max-w-lg mx-auto mt-10 border-rose-200 bg-rose-50/30">
          <Shield className="h-10 w-10 text-rose-500 mx-auto mb-3" />
          <h3 className="text-base font-bold text-slate-800 mb-1">
            Bạn không có quyền truy cập trang Quản lý Người dùng
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Tài khoản của bạn không được cấp quyền xem hoặc chỉnh sửa danh sách người dùng (users.view).
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: "/" })}>
            Về trang chủ
          </Button>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 p-2 md:p-4">
        <Card className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <span className="ml-2 text-slate-500">Đang tải dữ liệu...</span>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 p-2 md:p-4">
        <Card className="p-8 text-center text-red-500">
          Lỗi khi tải dữ liệu người dùng. Vui lòng thử lại.
        </Card>
      </div>
    );
  }

  return (
    <ListPageShell
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onToggleFilter={onToggleFilter}
      onClearAll={onClearAll}
      showFilter={showFilter}
      filterFields={userFilterFields}
      filterValues={filterValues}
      tabs={
        <div role="tablist" aria-label="Trạng thái tài khoản" className="flex items-center gap-1 shrink-0" data-testid="user-status-tabs">
          {[
            { key: "all", label: "Tất cả" },
            { key: "ACTIVE", label: "Đang hoạt động" },
            { key: "INACTIVE", label: "Ngừng hoạt động" },
          ].map((tab) => {
            const active = statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`user-status-tab-${tab.key}`}
                onClick={() => handleFilterChange("status", tab.key)}
                className={emsTabClass(active, { size: "sm" })}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      }
      onExport={canExportUsers ? () => handleExport() : undefined}
      exportLoading={isExporting}
      secondaryActions={
        canImportUsers
          ? [
              {
                key: "import-users",
                label: "Nhập Excel",
                icon: <Upload className="h-3.5 w-3.5 text-emerald-700" />,
                onClick: () => setShowImport(true),
              },
            ]
          : undefined
      }
      headerFilters={
        <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
          <ComboboxFK
            value={roleFilter === "all" ? "" : roleFilter}
            onChange={(val) => handleFilterChange("role", val || "all")}
            label=""
            hideLabel
            placeholder="Tất cả vai trò"
            options={
              roleOptions.length > 0
                ? roleOptions.map((r) => ({ id: r.id, label: r.label }))
                : [
                    { id: "ADMIN", label: "Quản trị viên" },
                    { id: "MANAGER", label: "Quản lý" },
                    { id: "SALES_MANAGER", label: "Trưởng phòng KD" },
                    { id: "SALES", label: "Nhân viên Kinh doanh" },
                    { id: "PLANNER", label: "Kế hoạch" },
                    { id: "WAREHOUSE", label: "Thủ kho" },
                    { id: "DELIVERY", label: "Giao hàng" },
                    { id: "QC", label: "Chất lượng (QC)" },
                    { id: "TECH", label: "Kỹ thuật" },
                    { id: "OPERATOR", label: "Vận hành máy" },
                    { id: "USER", label: "Nhân viên" },
                  ]
            }
            className="w-[155px] space-y-0 shrink-0 [&_button]:!h-7 [&_button]:!text-xs [&_button]:!px-2.5"
          />

          <ComboboxFK
            value={departmentFilter === "all" ? "" : departmentFilter}
            onChange={(val) => handleFilterChange("department", val || "all")}
            label=""
            hideLabel
            placeholder="Tất cả phòng ban"
            options={
              departmentOptions.length > 0
                ? departmentOptions.map((d) => ({ id: d.id, label: d.label }))
                : [
                    { id: "IT", label: "Công Nghệ Thông Tin" },
                    { id: "SALES", label: "Kinh Doanh" },
                    { id: "PROD", label: "Sản Xuất" },
                    { id: "QC", label: "Kiểm tra chất lượng (QC)" },
                    { id: "WH", label: "Kho Vận" },
                    { id: "TECH", label: "Kỹ Thuật" },
                    { id: "MAINT", label: "Bảo Trì" },
                    { id: "BOARD", label: "Ban Giám Đốc" },
                    { id: "FIN", label: "Kế Toán" },
                    { id: "HR", label: "Hành Chính Nhân Sự" },
                  ]
            }
            className="w-[160px] space-y-0 shrink-0 [&_button]:!h-7 [&_button]:!text-xs [&_button]:!px-2.5"
          />

          <ComboboxFK
            value={positionFilter === "all" ? "" : positionFilter}
            onChange={(val) => handleFilterChange("section", val || "all")}
            label=""
            hideLabel
            placeholder="Tất cả chức vụ"
            options={[
              { id: "Giám Đốc", label: "Giám Đốc" },
              { id: "Trưởng Phòng", label: "Trưởng Phòng" },
              { id: "Phó Phòng", label: "Phó Phòng" },
              { id: "Quản Lý", label: "Quản Lý" },
              { id: "Tổ Trưởng", label: "Tổ Trưởng" },
              { id: "Trưởng Ca", label: "Trưởng Ca" },
              { id: "Kế toán viên", label: "Kế toán viên" },
              { id: "Nhân viên", label: "Nhân viên" },
              { id: "Công nhân", label: "Công nhân" },
              { id: "Chuyên viên", label: "Chuyên viên" },
              { id: "Thực tập sinh", label: "Thực tập sinh" },
            ]}
            className="w-[145px] space-y-0 shrink-0 [&_button]:!h-7 [&_button]:!text-xs [&_button]:!px-2.5"
          />
        </div>
      }
      actions={
        canCreateUsers ? (
          <Button
            size="sm"
            onClick={() => navigate({ to: "/users/new" })}
            className="h-7 px-2.5 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shrink-0"
          >
            <UserPlus className="h-3.5 w-3.5 shrink-0" />
            <span>Thêm nhân sự</span>
          </Button>
        ) : undefined
      }
    >
      <Table className={cn(selectionMode && "apv-select-mode")}>
        <TableHeader className="apv-table-header">
          <TableRow className="border-b-0 hover:bg-transparent">
            <TableHead className={cn("apv-col-checkbox w-10 text-center")}>
              <DataTableSelectAllCheckbox
                rowSelection={
                  { allSelected, toggleSelectAll, selectionMode } as any
                }
                asChild
              />
            </TableHead>
            <SortableHeader
              label="Mã NV"
              field="employeeCode"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="apv-col-code w-24 text-slate-500 font-medium"
            />
            <SortableHeader
              label="Thông tin chung"
              field="fullName"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[280px] text-slate-500 font-medium"
            />
            <SortableHeader
              label="Số điện thoại"
              field="phone"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-32 text-slate-500 font-medium"
            />
            <SortableHeader
              label="Vai trò"
              field="role"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-32 text-slate-500 font-medium"
            />
            <SortableHeader
              label="Phòng ban"
              field="department"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-40 text-slate-500 font-medium"
            />
            <SortableHeader
              label="Trạng thái"
              field="status"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-28 text-center text-slate-500 font-medium"
            />
            <TableHead className="w-16 text-right"></TableHead>
          </TableRow>
          {showFilter && (
            <InlineFilterRow
              columns={[
                CHECKBOX_COL_NULL,
                { key: "employeeCode" },
                { key: "search" },
                { key: "phone" },
                { key: "role" },
                { key: "department" },
                { key: "status" },
                null,
              ]}
              filterFields={userFilterFields}
              filterValues={filterValues}
              onFilterChange={onFilterChange}
            />
          )}
        </TableHeader>
        <TableBody>
          {filteredUsers.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={selectionMode ? 9 : 8}
                className="h-24 text-center text-slate-500"
              >
                Không tìm thấy nhân viên nào.
              </TableCell>
            </TableRow>
          ) : (
            filteredUsers.map((user) => (
              <DataTableRow
                key={user.userId}
                id={user.userId}
                rowSelection={
                  {
                    selectedIds,
                    handleRowMouseDown,
                    handleRowMouseUp,
                    handleRowMouseLeave,
                    wasLongPress,
                    selectionMode,
                    handleRowSelect,
                  } as any
                }
                onClick={(e) => {
                  if (selectionMode) return;
                  navigate({
                    to: "/users/$userId",
                    params: { userId: user.userId },
                  });
                }}
              >
                <TableCell
                  className={cn("apv-col-checkbox")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DataTableRowCheckbox
                    id={user.userId}
                    asChild
                    rowSelection={{ selectedIds, toggleSelect } as any}
                  />
                </TableCell>
                <TableCell className="apv-col-code">
                  <div className="font-mono text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded inline-block">
                    {user.employeeCode || user.userId}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9 border border-slate-100 shadow-sm">
                      <AvatarImage src={getUploadUrl(user.avatar) || ""} />
                      <AvatarFallback className="bg-emerald-50 text-emerald-700 font-semibold">
                        {user.fullName.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="font-bold text-slate-800 truncate">
                        {user.fullName}
                      </span>
                      <div className="text-[12px] text-slate-500 truncate mt-0.5">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {user.phone || "-"}
                </TableCell>
                <TableCell>{getRoleBadge(user.role)}</TableCell>
                <TableCell className="text-sm text-slate-600">
                  <div className="font-medium text-slate-700">
                    {getDepartmentLabel(user.department)}
                  </div>
                  {user.section &&
                    user.section !== getDepartmentLabel(user.department) && (
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {user.section}
                      </div>
                    )}
                </TableCell>
                <TableCell className="text-center">
                  {(() => {
                    const sCfg = getStatusConfig("UserStatus", user.status);
                    return (
                      <Badge
                        variant="secondary"
                        className={cn(
                          "font-semibold text-[10px] uppercase border-none px-2",
                          sCfg?.color || "bg-slate-100 text-slate-500",
                        )}
                      >
                        {sCfg?.label || user.status}
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  className="text-right"
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-slate-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-52 shadow-xl border-slate-200"
                    >
                      <DropdownMenuLabel className="text-xs text-slate-500">
                        Quản lý tài khoản
                      </DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleEdit(user)}>
                        <Pencil className="mr-2 h-4 w-4 text-slate-400" /> Chỉnh
                        sửa thông tin
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </DataTableRow>
            ))
          )}
        </TableBody>
      </Table>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        title="Nhập dữ liệu Nhân sự"
        columns={EXPORT_COLUMNS}
        onImport={handleImport}
        refs={userImportRefs}
        onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "NhanSu_Mau")}
      />
    </ListPageShell>
  );
}
